export type CityPartner = {
  name: string;
  type: "Domaine" | "Salle" | "Château" | "Mas" | "Hôtel" | "Restaurant" | "Traiteur" | "Wedding Planner" | "Office de Tourisme";
  desc: string;
  url?: string;
};

export type City = {
  slug: string;
  name: string;
  region: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  hero: string;
  highlights: { title: string; text: string }[];
  venues: string[];
  partners?: CityPartner[];
  caterers?: CityPartner[];
  localInsight?: string;
  /** Sections rédigées propres à la ville (unicité éditoriale, anti-doorway). */
  sections?: { h2: string; paragraphs: string[] }[];
  /** Retour d'expérience concret sur une prestation dans la ville. */
  caseStudy?: { title: string; text: string };
  faq: { q: string; a: string }[];
  preposition?: string;
};


const HERO_DEFAULT =
  "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=1200&q=70&fm=webp&auto=compress&fit=crop";

export const CITIES: Record<string, City> = {
  avignon: {
    slug: "avignon",
    name: "Avignon",
    region: "Vaucluse",
    title: "DJ Mariage Avignon",
    metaTitle: "DJ Avignon : mariage & soirée | DJ Val",
    metaDescription:
      "DJ à Avignon (Vaucluse) pour mariage, soirée privée et événement d'entreprise. Sono + éclairage LED inclus. 20 ans d'expérience. Devis 24 h.",

    h1: "DJ Mariage à Avignon",
    intro:
      "Spécialiste de l'animation de mariage à Avignon depuis plus de 20 ans. Du palais des Papes aux mas provençaux, je crée une ambiance unique qui réveille la piste jusqu'au bout de la nuit.",
    hero: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    highlights: [
      {
        title: "Connaissance des lieux",
        text: "Plus de 200 mariages animés dans les domaines emblématiques d'Avignon : Mas de la Rose, Domaine de Manville, Château de Massillan…",
      },
      {
        title: "Logistique intra-muros",
        text: "Installation discrète et rapide même dans les lieux contraints du centre historique d'Avignon.",
      },
      {
        title: "Réseau de prestataires",
        text: "Photographes, traiteurs, wedding planners avignonnais avec qui je travaille en confiance depuis des années.",
      },
    ],
    venues: ["Mas de la Rose", "Domaine de Manville", "Château de Massillan", "Hôtel La Mirande", "Domaine de Mejeans"],
    localInsight:
      "Avignon est à 25 minutes de Cavaillon par la D973. Intra-muros, tout se joue sur l'accès : livraison du matériel avant 19 h, stationnement temporaire à obtenir auprès du lieu, et souvent un portage à la main sur les derniers mètres pavés.",
    sections: [
      {
        h2: "Se marier intra-muros ou en périphérie d'Avignon",
        paragraphs: [
          "Les réceptions avignonnaises se répartissent en deux mondes très différents. À l'intérieur des remparts, on est dans des hôtels particuliers, des cours intérieures et des salles voûtées : la pierre réverbère, les volumes sont hauts et étroits, et le voisinage est immédiat. Le son doit y être resserré, directif, avec des basses tenues courtes pour ne pas transformer une cour en caisse de résonance.",
          "En périphérie — route de Montfavet, Villeneuve-lès-Avignon, Les Angles, Le Pontet — on retrouve des mas et des domaines avec de grands extérieurs. Là, la difficulté s'inverse : le son se disperse, il faut plus de points de diffusion et une lumière capable de dessiner un vrai dancefloor dans un jardin ouvert.",
          "Je repère systématiquement le lieu en amont : hauteur sous plafond, nature des murs, position du bar, distance entre le repas et la piste. C'est ce repérage qui fait la différence entre une soirée correcte et une piste pleine à 2 h du matin.",
        ],
      },
      {
        h2: "Horaires, nuisances sonores et fin de soirée à Avignon",
        paragraphs: [
          "Avignon est une ville habitée, y compris dans son centre historique. Sur les lieux intra-muros, la plupart des contrats de location imposent un arrêt de la musique amplifiée entre 1 h et 2 h, parfois avec un limiteur en place. En périphérie, les domaines sont plus souples, mais les riverains restent proches sur plusieurs adresses.",
          "Ma méthode est toujours la même : je cale le système en début de soirée avec le responsable du lieu, je surveille le niveau en continu et je place le subwoofer de manière à éviter la propagation vers les habitations. La fête ne s'arrête pas plus tôt — elle est simplement calibrée pour ne pas provoquer d'intervention.",
          "Pour les mariages qui souhaitent aller au-delà de l'horaire légal, la solution consiste à prévoir une fin de soirée en intérieur, portes fermées, avec un niveau réduit et une sélection plus intimiste.",
        ],
      },
    ],
    caseStudy: {
      title: "Un mariage dans une cour intérieure avignonnaise",
      text: "Cérémonie laïque en fin d'après-midi dans une cour pavée intra-muros, cocktail sous les arcades, puis dîner et soirée dans la même cour. Deux contraintes : la réverbération de la pierre et un arrêt imposé à 1 h. J'ai travaillé en diffusion rapprochée, quatre points de faible puissance plutôt que deux gros, micros HF pour l'officiant et les témoins, puis une bascule progressive vers la piste après le dessert. Résultat : aucun retour du voisinage, et une fin de soirée acoustique prolongée à l'intérieur pour les derniers invités.",
    },
    faq: [
      {
        q: "Intervenez-vous dans tout le Vaucluse ?",
        a: "Oui, Avignon, Le Pontet, Sorgues, Châteaurenard, Carpentras et tous les villages alentour. Aucun frais de déplacement dans un rayon de 80 km.",
      },
      {
        q: "Quel est le tarif moyen pour un mariage à Avignon ?",
        a: "Comptez entre 1 490 € et 2 500 € selon la durée, l'équipement (sono, lumière, photobooth) et le nombre d'invités. Devis personnalisé gratuit.",
      },
      {
        q: "Comment se passe l'installation dans un lieu intra-muros ?",
        a: "J'arrive plus tôt qu'ailleurs, généralement 3 à 4 heures avant les invités, car l'accès aux rues du centre est réglementé et le déchargement se fait souvent à distance de la salle. Je préviens le lieu en amont pour obtenir l'autorisation de stationnement temporaire.",
      },
      {
        q: "Jusqu'à quelle heure peut-on faire la fête à Avignon ?",
        a: "Intra-muros, la plupart des lieux imposent l'arrêt de la musique amplifiée entre 1 h et 2 h, parfois avec limiteur. Dans les domaines de la périphérie (Villeneuve-lès-Avignon, Montfavet, Les Angles), on va généralement jusqu'à 3 h ou 4 h selon le contrat de location.",
      },
    ],

  },
  cavaillon: {
    slug: "cavaillon",
    name: "Cavaillon",
    region: "Vaucluse",
    title: "DJ Mariage Cavaillon",
    metaTitle: "DJ Cavaillon : mariage & soirée | DJ Val",
    metaDescription:
      "DJ basé à Cavaillon : mariage, soirée privée, anniversaire, location sono & éclairage dans le Luberon et le Vaucluse. 20 ans d'expérience. Devis sous 24 h.",
    h1: "DJ Mariage à Cavaillon",
    intro:
      "Basé à Cavaillon, j'anime vos mariages dans tout le Vaucluse et le Luberon. Une connaissance fine du terroir et des domaines locaux pour une soirée qui vous ressemble.",
    hero: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    highlights: [
      { title: "Local à 100%", text: "Cavaillon est mon port d'attache. Aucun déplacement, aucun supplément, une réactivité totale." },
      { title: "Domaines partenaires", text: "Mas des Comtes de Provence, Domaine de Tara, Le Mas de la Chapelle… je connais chaque salle, chaque dancefloor." },
      { title: "Multi-générations", text: "De vos grands-parents à vos cousins ados, je sais lire votre dancefloor et adapter chaque transition." },
    ],
    venues: ["Mas des Comtes de Provence", "Domaine de Tara", "Le Mas de la Chapelle", "Château la Verrerie"],
    faq: [
      { q: "Quels sont vos délais de réservation ?", a: "Pour un mariage en juin, juillet, août ou septembre à Cavaillon, réservez 9 à 12 mois à l'avance." },
      { q: "Proposez-vous l'animation de la cérémonie laïque ?", a: "Oui, sonorisation HF, micros sans fil, musiques d'entrée/sortie. Forfait cérémonie disponible." },
    ],
  },
  luberon: {
    slug: "luberon",
    name: "Luberon",
    region: "Vaucluse",
    preposition: "dans le",
    title: "DJ Mariage Luberon",
    metaTitle: "DJ mariage Luberon : domaines & mas | DJ Val",
    metaDescription:
      "DJ mariage Luberon : Gordes, Bonnieux, Lourmarin, Roussillon. 20 ans d'expérience, sono pro, éclairage LED, gestion acoustique. Devis 24 h.",

    h1: "DJ Mariage dans le Luberon",
    intro:
      "Le Luberon n'est pas qu'un décor — c'est une acoustique, une lumière, une façon de vivre la fête. Entre les villages perchés de Gordes et Ménerbes, les oliveraies de Lourmarin et les vignes de Roussillon, chaque domaine a sa personnalité. Depuis plus de 20 ans, j'anime des mariages dans le Luberon avec une approche sur mesure : son cristallin pour la cérémonie, ambiance lounge pour le cocktail, et une piste de danse qui ne désemplit pas jusqu'au bout de la nuit.",
    hero: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1600&q=75&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Un mariage au Luberon, ce n'est jamais une prestation standard. Le mistral, les pierres qui réverbèrent, les domaines en zone Natura 2000, les voisins à 200 m… J'interviens toujours avec un repérage technique en amont, un limiteur calibré et un plan B intérieur validé avec le couple.",
    highlights: [
      {
        title: "60+ mariages dans le Luberon",
        text: "Bastide de Marie, Coquillade Provence, Domaine de Capelongue, Bastide de Gordes, Mas de la Sénancole, Domaine de la Citadelle… Je connais les contraintes acoustiques, électriques et logistiques de chacun.",
      },
      {
        title: "Cocktail & cérémonie en extérieur",
        text: "Sono discrète sur batterie pour la cérémonie laïque sous les oliviers, micros HF Shure pour les échanges de consentement, ambiance jazz/lounge face au Luberon pour le vin d'honneur.",
      },
      {
        title: "Voisinage & limites sonores maîtrisés",
        text: "Limiteur calibré, sonomètre en continu, subwoofer directionnel et communication avec les gérants de domaine. La fête dure sans coupure, dans le respect des riverains.",
      },
    ],
    venues: [
      "Bastide de Marie",
      "Coquillade Provence",
      "Domaine de Capelongue",
      "Bastide de Gordes",
      "Mas de la Sénancole",
      "Domaine de la Citadelle",
      "Domaine des Andéols",
      "Mas de la Rose",
    ],
    partners: [
      {
        name: "Bastide de Marie",
        type: "Domaine",
        desc: "Hôtel 5* à Ménerbes avec vue panoramique sur le Luberon. Jardin à la française, salle voûtée et terrasse pour des mariages de 60 à 150 invités. L'un des lieux les plus photographiés de Provence.",
        url: "https://www.bastidedemarie.fr",
      },
      {
        name: "Coquillade Provence Resort & Spa",
        type: "Domaine",
        desc: "Resort 5* à Gargas avec plusieurs salles de réception, hébergement sur place et vue à 360° sur les vignes du Luberon. Idéal pour des mariages multi-jours avec guests internationaux.",
        url: "https://www.coquillade.fr",
      },
      {
        name: "Domaine de Capelongue",
        type: "Domaine",
        desc: "Ancienne bastide restaurée à Bonnieux, jardins potagers et vue imprenable sur le village. Ambiance intimiste et gastronomique, parfaite pour des mariages de 40 à 120 invités.",
        url: "https://www.capelongue.com",
      },
      {
        name: "La Bastide de Gordes",
        type: "Hôtel",
        desc: "Palais provençal suspendu au-dessus de Gordes, classé 5*. Terrasses en cascade, spa et salles historiques pour des mariages d'exception dans un cadre spectaculaire.",
        url: "https://www.bastide-de-gordes.com",
      },
      {
        name: "Mas de la Sénancole",
        type: "Mas",
        desc: "Mas authentique en pierre entre Gordes et Goult, avec une cour intérieure typique et un grand pré pour les réceptions en plein air. Ambiance champêtre chic et intime.",
        url: "https://www.masdelasenancole.com",
      },
      {
        name: "Domaine de la Citadelle",
        type: "Domaine",
        desc: "Domaine viticole à Ménerbes avec salle contemporaine, terrasse sur les vignes et cave pour les dégustations. Un lieu élégant et peu connu, parfait pour des mariages confidentiels.",
        url: "https://www.domainedelacitadelle.com",
      },
    ],
    caterers: [
      {
        name: "Maison Nard",
        type: "Traiteur",
        desc: "Traiteur historique de Lourmarin, réputé pour ses buffets provençaux raffinés et son sens du service en extérieur. Croisé sur de nombreux mariages dans le Luberon.",
      },
      {
        name: "La Cour des Sens",
        type: "Traiteur",
        desc: "Cuisine gastronomique du Pays d'Apt, spécialisée dans les mariages haut de gamme du Luberon. Présentation soignée et produits locaux.",
      },
      {
        name: "Traiteur Bontemps",
        type: "Traiteur",
        desc: "Basé à Cavaillon, famille Bontemps traiteur depuis 30 ans. Cuisine provençale généreuse, parfaite pour les grands dîners de mariage en bastide.",
      },
      {
        name: "Le Moulin de Lourmarin",
        type: "Restaurant",
        desc: "Restaurant emblématique de Lourmarin, proposant aussi des prestations traiteur pour des réceptions privées dans le Luberon.",
      },
    ],
    faq: [
      {
        q: "Quels sont les plus beaux domaines pour se marier dans le Luberon ?",
        a: "Les couples choisissent souvent la Bastide de Marie à Ménerbes, Coquillade Provence à Gargas, le Domaine de Capelongue à Bonnieux, la Bastide de Gordes, le Mas de la Sénancole ou le Domaine de la Citadelle. Chacun offre un style différent : palace 5*, bastide intimiste, mas champêtre ou domaine viticole confidentiel.",
      },
      {
        q: "Comment gérez-vous le mistral lors d'un mariage en extérieur dans le Luberon ?",
        a: "Le mistral peut souffler fort en mai-juin. Je sécurise les structures légères (pieds de micros, enceintes sur trépieds, guirlandes), je prévois un plan B intérieur ou semi-couvert avec le couple, et j'utilise des enceintes autonomes sur batterie pour la cérémonie si l'électricité est éloignée.",
      },
      {
        q: "Quelles sont les contraintes sonores dans les villages perchés du Luberon ?",
        a: "Beaucoup de domaines du Luberon sont proches de zones résidentielles ou classés Natura 2000. Les arrêtés préfectoraux imposent souvent un arrêt de la musique extérieure à 23 h ou 1 h, avec des seuils à 95 dB. Je travaille systématiquement avec un limiteur calibré et un sonomètre pour éviter toute coupure.",
      },
      {
        q: "Peut-on faire un mariage dans le Luberon avec un budget modéré ?",
        a: "Oui. Au-delà des palaces, le Luberon regorge de mas familiaux, de domaines viticoles et de salles communales moins médiatisés. Ma formule Essentielle à partir de 1 490 € convient à de nombreux couples, avec un DJ + sono + éclairage clé en main pour jusqu'à 120 invités.",
      },
      {
        q: "Quelle est la meilleure saison pour se marier dans le Luberon ?",
        a: "Mai-juin et septembre sont idéaux : lumière dorée, températures agréables, vignes et lavandes en tenue. Juillet-août est magnifique mais très chaud et très demandé — il faut réserver 12 à 14 mois à l'avance. Avril et octobre offrent aussi de belles journées ensoleillées avec moins de monde.",
      },
      {
        q: "Comment coordonner DJ, traiteur et photographe dans un domaine du Luberon ?",
        a: "Je coordonne directement avec le traiteur pour les temps de service, les discours et l'ouverture de bal. Avec le photographe, on calle les moments lumière (coucher de soleil, étincelles froides, première danse). Un planning technique partagé est envoyé à tous les prestataires une semaine avant le jour J.",
      },
      {
        q: "Proposez-vous la sonorisation de cérémonie laïque en extérieur dans le Luberon ?",
        a: "Oui, c'est même l'un de mes points forts. Deux enceintes autonomes Bose S1 Pro ou Electro-Voice Everse 8, deux micros HF Shure discrets, diffusion musicale haute fidélité. J'installe 30 min avant l'arrivée des invités et je reste présent pendant toute la cérémonie.",
      },
      {
        q: "Quel est votre tarif pour un mariage dans le Luberon ?",
        a: "Comptez entre 1 490 € et 2 500 € selon la durée, le nombre d'invités, les options (cérémonie laïque, lumière d'ambiance, photobooth, étincelles froides) et la distance depuis Cavaillon. Devis personnalisé sous 24 h.",
      },
    ],
  },
  alpilles: {
    slug: "alpilles",
    name: "Alpilles",
    region: "Bouches-du-Rhône",
    preposition: "dans les",
    title: "DJ Mariage Alpilles",
    metaTitle: "DJ mariage Alpilles & Saint-Rémy | DJ Val",
    metaDescription:
      "DJ professionnel pour vos mariages dans les Alpilles : Saint-Rémy-de-Provence, Les Baux, Eygalières, Maussane. Animation premium et matériel haut de gamme.",
    h1: "DJ Mariage dans les Alpilles",
    intro:
      "Saint-Rémy, Les Baux, Eygalières, Maussane — les Alpilles offrent un cadre romantique unique. J'y anime des mariages qui mêlent élégance provençale et énergie de la fête.",
    hero: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    highlights: [
      { title: "Domaines prestigieux", text: "Mas de la Rose, Château d'Estoublon, Mas des Petites Crottes, Domaine de Manville." },
      { title: "Ambiance bohème ou raffinée", text: "Du chic provençal au dancefloor déchaîné — je m'adapte à votre univers." },
      { title: "Photobooth & guirlandes", text: "Décor lumineux guinguette en option pour transformer votre cour en piste de danse intemporelle." },
    ],
    venues: ["Mas de la Rose", "Château d'Estoublon", "Domaine de Manville", "Mas des Petites Crottes"],
    faq: [
      { q: "Intervenez-vous à Saint-Rémy-de-Provence ?", a: "Oui, c'est l'une de mes zones favorites. J'y anime 15 à 20 mariages par an." },
      { q: "Avez-vous un éclairage adapté à un mariage en extérieur ?", a: "Oui : guirlandes guinguette, projecteurs LED de couleur, lyres pour le dancefloor, éclairages architecturaux." },
    ],
  },
  "aix-en-provence": {
    slug: "aix-en-provence",
    name: "Aix-en-Provence",
    region: "Bouches-du-Rhône",
    title: "DJ Mariage Aix-en-Provence",
    metaTitle: "DJ Aix-en-Provence mariage dès 1490 € · 5,0★ | DJ Val",
    metaDescription:
      "DJ mariage à Aix-en-Provence dès 1 490 € : sono et éclairage LED inclus, 200+ mariages, note Google 5,0. Un seul événement par week-end. Devis gratuit en 24 h.",
    h1: "DJ Mariage à Aix-en-Provence",
    intro:
      "DJ à Aix-en-Provence depuis plus de 20 ans, j'anime les mariages des bastides du XVIIIᵉ, des domaines viticoles des Coteaux d'Aix et des villas de la campagne aixoise. Sonorisation RCF, éclairage LED, micros HF et système autonome pour la cérémonie : tout est inclus, du vin d'honneur à la dernière danse.",
    hero: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Aix-en-Provence est à 55 km de Cavaillon, soit 50 minutes par l'A7 puis l'A51 : aucun frais de déplacement. Je couvre Aix et sa campagne — Venelles, Éguilles, Meyreuil, Rousset, Le Puy-Sainte-Réparade, Vauvenargues, Saint-Cannat, Ventabren — ainsi que les domaines viticoles des Coteaux d'Aix.",
    highlights: [
      { title: "Réceptions chic", text: "Habitué des bastides aixoises et des villas de la campagne aixoise, je sais m'adapter à un public exigeant sans jamais tomber dans le medley de mariage." },
      { title: "Cocktail & dîner soignés", text: "Playlist lounge maîtrisée pour le cocktail, ambiance crescendo au dîner, dancefloor déchaîné après le gâteau." },
      { title: "Système autonome sur batterie", text: "Cérémonie sous les chênes, cocktail près du bassin : diffusion mobile sans tirer 60 mètres de câble en travers des allées." },
      { title: "Limiteur maîtrisé", text: "Beaucoup de domaines aixois sont équipés d'un limiteur à demeure. Je récupère la fiche technique en amont et je calibre pour ne jamais couper la musique." },
      { title: "Discrétion absolue", text: "Tenue élégante, installation propre, communication fluide avec votre wedding planner et le traiteur." },
      { title: "1 seul mariage par week-end", text: "Pas de double date : repérage du lieu offert et disponibilité totale le jour J." },
    ],
    venues: ["Domaine de Fontenille", "Villa Gallici", "Domaine de la Crémade", "Bastide du Cours", "Château La Coste", "Domaine de Saint-Ser", "Villa Baulieu"],
    partners: [
      { name: "Office de Tourisme d'Aix-en-Provence", type: "Office de Tourisme", desc: "Informations officielles sur les lieux de réception du Pays d'Aix.", url: "https://www.aixenprovencetourism.com" },
      { name: "Château La Coste", type: "Domaine", desc: "Domaine viticole et art contemporain au Puy-Sainte-Réparade, réceptions d'exception.", url: "https://chateau-la-coste.com" },
      { name: "Domaine de Fontenille", type: "Domaine", desc: "Bastide provençale et domaine viticole aux portes du Luberon et du Pays d'Aix.", url: "https://www.domainedefontenille.com" },
    ],
    faq: [
      { q: "Travaillez-vous avec les wedding planners d'Aix ?", a: "Oui, je collabore régulièrement avec les agences locales du Pays d'Aix. Sur demande je peux fournir des références vérifiables." },
      { q: "Acceptez-vous les playlists imposées ?", a: "Bien sûr. On définit ensemble une trame, vos must-play et vos no-go. Vous restez maîtres de votre soirée, j'ajuste en direct selon la piste." },
      { q: "Quel est le tarif d'un DJ mariage à Aix-en-Provence ?", a: "À partir de 1 490 € en formule tout-inclus : sonorisation, éclairage LED, micros HF, installation et démontage. Le tarif final dépend de la durée et des options (photobooth, étincelles froides, guirlandes)." },
      { q: "Jusqu'à quelle heure peut-on danser dans un domaine aixois ?", a: "La plupart des domaines de la campagne aixoise autorisent la musique jusqu'à 2 h ou 3 h, souvent avec un limiteur installé à demeure. Je cale le système sous le seuil dès le début de soirée pour éviter toute coupure." },
    ],
  },
  "bouches-du-rhone": {
    slug: "bouches-du-rhone",
    name: "Bouches-du-Rhône",
    region: "PACA",
    preposition: "dans les",
    title: "DJ Mariage Bouches-du-Rhône",
    metaTitle: "DJ mariage Bouches-du-Rhône | DJ Val",
    metaDescription:
      "DJ professionnel pour mariages dans les Bouches-du-Rhône : Marseille, Aix, Arles, Salon, Martigues. Sonorisation, lumière, animation premium.",
    h1: "DJ Mariage dans les Bouches-du-Rhône",
    intro:
      "De Marseille à Arles en passant par Aix, Salon et la Camargue, j'anime vos mariages partout dans les Bouches-du-Rhône avec un matériel haut de gamme.",
    hero: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    highlights: [
      { title: "Couverture totale", text: "Marseille, Aix, Arles, Salon, Martigues, Aubagne, La Ciotat, Cassis — tout le département." },
      { title: "Matériel mobile pro", text: "2 véhicules équipés, backup complet, intervention même dans les lieux difficiles d'accès." },
      { title: "Multiculturel", text: "Habitué aux mariages mixtes : franco-italiens, franco-arméniens, franco-maghrébins, franco-asiatiques." },
    ],
    venues: ["Château La Coste", "Domaine de Sarson", "Villa Baulieu", "Domaine de la Tuilière"],
    faq: [
      { q: "Tarif déplacement pour Marseille ou Arles ?", a: "Un forfait déplacement raisonnable est appliqué au-delà de 50 km depuis Cavaillon. Devis transparent." },
      { q: "Disponible le dimanche ou en semaine ?", a: "Oui, je travaille 7j/7. Mariages civils, vins d'honneur en semaine, soirées d'entreprise du lundi au vendredi." },
    ],
  },
  evangelique: {
    slug: "evangelique",
    name: "Mariage Évangélique",
    region: "Provence",
    title: "DJ Mariage Évangélique Provence",
    metaTitle: "DJ mariage évangélique Provence | DJ Val",
    metaDescription:
      "DJ pour mariage évangélique en Provence et Vaucluse. Sélection musicale respectueuse, gestion des temps de louange, animation familiale.",
    h1: "DJ pour Mariage Évangélique en Provence",
    intro:
      "Un mariage évangélique demande sensibilité, écoute et un répertoire adapté. J'accompagne depuis 10 ans les couples chrétiens de Provence avec une sélection musicale qui respecte vos valeurs.",
    hero: "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    highlights: [
      { title: "Répertoire adapté", text: "Sélection sans textes vulgaires, équilibre entre louange, musique francophone, classiques internationaux." },
      { title: "Sono pour la louange", text: "Micros HF pour le groupe de louange, console adaptée, gestion des transitions cérémonie → repas → soirée." },
      { title: "Respect de votre communauté", text: "Échange en amont avec votre pasteur pour caler la trame musicale de la cérémonie." },
    ],
    venues: ["Domaines chrétiens partenaires", "Salles communales", "Mas privés"],
    faq: [
      { q: "Acceptez-vous une cérémonie sans alcool / sans danse 'frontale' ?", a: "Bien sûr. Je m'adapte à votre cadre. L'objectif reste la joie des invités et le respect de votre engagement." },
      { q: "Pouvez-vous gérer la sonorisation du temple ou de la salle de culte ?", a: "Oui, intervention en amont pour la cérémonie religieuse puis transition vers la salle de réception." },
    ],
  },

  // ============== 19 NOUVELLES VILLES (migration WP→Lovable) ==============

  apt: {
    slug: "apt",
    name: "Apt",
    region: "Vaucluse",
    title: "DJ Mariage Apt",
    metaTitle: "DJ Apt : mariage & soirée en Luberon | DJ Val",
    metaDescription:
      "DJ professionnel à Apt (84) pour mariage, anniversaire et soirée privée. Cœur du Luberon, domaines et mas environnants. 20 ans d'expérience, devis 24 h.",
    h1: "DJ Mariage à Apt",
    intro:
      "Apt, capitale historique du Pays d'Apt-Luberon, regroupe certains des plus beaux mas et domaines viticoles du Vaucluse. J'y interviens depuis plus de 20 ans pour des mariages où le cadre naturel impose élégance et discrétion sonore.",
    hero: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "À 35 km de Cavaillon par la D900, Apt est un point d'ancrage idéal pour rayonner sur Roussillon, Bonnieux, Saignon, Goult et Gordes. Aucun frais de déplacement supplémentaire.",
    highlights: [
      { title: "Cœur du Luberon", text: "Connaissance fine des domaines aptésiens et de leurs contraintes acoustiques (limiteur, voisinage village)." },
      { title: "Mariages en ocre", text: "Habitué des cadres minéraux et lumineux du pays d'Apt : Roussillon, Rustrel, Saignon." },
      { title: "Cocktails sous les platanes", text: "Sono lounge discrète pour les vins d'honneur en terrasse, basculement progressif vers la piste à la tombée du jour." },
    ],
    venues: ["Domaine de Tara", "Coquillade Provence", "Mas Perreal", "Domaine des Andéols"],
    partners: [
      {
        name: "Coquillade Provence Resort & Spa",
        type: "Domaine",
        desc: "Resort 5★ avec vue panoramique sur le Luberon, plusieurs salles de réception jusqu'à 200 invités.",
        url: "https://www.coquillade.fr",
      },
      {
        name: "Domaine de Tara",
        type: "Domaine",
        desc: "Domaine viticole familial entre Apt et Roussillon, idéal pour mariages champêtres en extérieur.",
        url: "https://www.domainedetara.com",
      },
      {
        name: "Office de Tourisme Pays d'Apt Luberon",
        type: "Office de Tourisme",
        desc: "Ressource officielle pour identifier salles et hébergements groupes autour d'Apt.",
        url: "https://www.luberon-apt.fr",
      },
    ],
    caterers: [
      { name: "Maison Gouin", type: "Traiteur", desc: "Boucherie-traiteur historique d'Apt, prestations mariage et cocktails." },
      { name: "Lou Magau", type: "Traiteur", desc: "Traiteur gastronomique du Pays d'Apt, cuisine provençale signature." },
    ],
    faq: [
      { q: "Intervenez-vous dans tout le Pays d'Apt ?", a: "Oui : Apt, Roussillon, Bonnieux, Saignon, Rustrel, Gargas, Goult, Lacoste, Ménerbes. Inclus dans le forfait standard." },
      { q: "Les domaines d'Apt imposent-ils des limites de bruit ?", a: "Oui, la plupart des mas en zone rurale plafonnent autour de 95 dB. Je viens systématiquement avec un limiteur calibré et un sonomètre." },
      { q: "Combien de temps pour rejoindre Apt depuis Cavaillon ?", a: "35 minutes par la D900. Installation possible dès le matin pour les mariages avec cérémonie laïque sur place." },
    ],
  },


  arles: {
    slug: "arles",
    name: "Arles",
    region: "Bouches-du-Rhône",
    title: "DJ Mariage Arles",
    metaTitle: "DJ Arles – Mariage Camargue & Pays d'Arles | DJ Val",
    metaDescription:
      "DJ professionnel à Arles (13) pour mariage en Camargue, Pays d'Arles, Alpilles. Mas, domaines et manades. Animation pro, devis 24 h.",
    h1: "DJ Mariage à Arles",
    intro:
      "Arles, ville UNESCO entre Alpilles et Camargue, offre un cadre unique pour les mariages. Manades, mas camarguais, abbayes — chaque lieu a son tempo. J'y interviens depuis plus de 12 ans avec une approche sur mesure.",
    hero: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "À 45 minutes de Cavaillon par l'A7. Couverture complète : Arles intra-muros, Saintes-Maries, Salin-de-Giraud, Fontvieille, Tarascon.",
    highlights: [
      { title: "Mariages camarguais", text: "Habitué des manades et mas de Camargue, avec leurs contraintes spécifiques (extérieur, mistral, électricité)." },
      { title: "Cadres UNESCO", text: "Cour de l'archevêché, cloître Saint-Trophime, hôtels particuliers — animation discrète et respectueuse du patrimoine." },
      { title: "Cocktails sur les arènes", text: "Spectacles de gardians, abrivado, ambiance taurine — je sais composer avec une fête typée Camargue." },
    ],
    venues: ["Mas de Peint", "Domaine de Manville", "Hôtel Jules César", "Abbaye de Montmajour"],
    partners: [
      {
        name: "Mas de Peint",
        type: "Mas",
        desc: "Mas camarguais de prestige, élevage de taureaux, mariage en pleine nature.",
        url: "https://www.masdepeint.com",
      },
      {
        name: "L'Hôtel Jules César MGallery",
        type: "Salle",
        desc: "Ancien couvent réinventé par Christian Lacroix, salles de réception au cœur d'Arles.",
        url: "https://www.hotel-julescesar.fr",
      },
      {
        name: "Office de Tourisme d'Arles",
        type: "Office de Tourisme",
        desc: "Annuaire officiel des lieux de réception du Pays d'Arles.",
        url: "https://www.arlestourisme.com",
      },
    ],
    caterers: [
      { name: "Le Cilantro Traiteur", type: "Traiteur", desc: "Traiteur arlésien gastronomique, accent provençal et camarguais." },
      { name: "L'Atelier de Jean-Luc Rabanel", type: "Traiteur", desc: "Cuisine étoilée déclinée en prestation mariage haut de gamme." },
    ],
    faq: [
      { q: "Mariage en manade en Camargue, équipement ?", a: "Sonorisation tropicalisée, groupe électrogène insonorisé sur demande, câblage sécurisé pour les sols meubles." },
      { q: "Animation pendant la ferrade ou l'abrivado ?", a: "Oui, sono mobile + micro HF pour accompagner les jeux gardians. Transition fluide vers le dîner et le dancefloor." },
    ],
  },

  orange: {
    slug: "orange",
    name: "Orange",
    region: "Vaucluse",
    title: "DJ Mariage Orange",
    metaTitle: "DJ Orange – Mariage & Soirée Haut-Vaucluse | DJ Val",
    metaDescription:
      "DJ à Orange (84) pour mariage, anniversaire et événement d'entreprise. Théâtre antique, châteaux des Côtes du Rhône. Devis sous 24 h.",
    h1: "DJ Mariage à Orange",
    intro:
      "Orange, ville romaine au cœur des Côtes du Rhône, regorge de châteaux viticoles et de mas pour vos réceptions. J'y interviens régulièrement avec une expérience approfondie des domaines de Châteauneuf-du-Pape, Gigondas et Vacqueyras.",
    hero: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Orange est à 45 minutes de Cavaillon. Forfait standard, aucun supplément pour les mariages dans le triangle Orange-Avignon-Carpentras.",
    highlights: [
      { title: "Châteaux viticoles", text: "Châteauneuf-du-Pape, Gigondas, Vacqueyras, Beaumes-de-Venise — un terrain de jeu privilégié depuis 20 ans." },
      { title: "Mariages d'envergure", text: "Domaines capables d'accueillir 200 à 400 invités — sono line-array adaptée, double zone son." },
      { title: "Mistral & extérieur", text: "Équipement renforcé pour les terrasses orangeoises soumises au mistral — fixations sécurisées, backup batterie." },
    ],
    venues: ["Château de Beaucastel", "Domaine de Mont-Redon", "Château La Nerthe", "Mas de Boudan"],
    partners: [
      {
        name: "Château La Nerthe",
        type: "Château",
        desc: "Domaine historique de Châteauneuf-du-Pape, réceptions mariage dans le cellier voûté.",
        url: "https://www.chateaulanerthe.fr",
      },
      {
        name: "Office de Tourisme Pays d'Orange",
        type: "Office de Tourisme",
        desc: "Annuaire officiel des lieux et prestataires mariage du Haut-Vaucluse.",
        url: "https://www.orange-tourisme.fr",
      },
    ],
    caterers: [
      { name: "Le Parvis Traiteur", type: "Traiteur", desc: "Traiteur orangeois reconnu pour ses prestations mariage et événementiel." },
    ],
    faq: [
      { q: "Intervenez-vous à Châteauneuf-du-Pape et Gigondas ?", a: "Oui, ces domaines sont dans ma zone privilégiée. Tarif inclus dans le forfait standard depuis Cavaillon." },
      { q: "Pouvez-vous gérer un mariage de 300+ invités ?", a: "Oui, sono line-array RCF HDL, plusieurs zones de diffusion, micros HF supplémentaires. Devis adapté à la capacité." },
    ],
  },

  "saint-remy": {
    slug: "saint-remy",
    name: "Saint-Rémy-de-Provence",
    region: "Bouches-du-Rhône",
    title: "DJ Mariage Saint-Rémy-de-Provence",
    metaTitle: "DJ Saint-Rémy-de-Provence – Mariage Alpilles | DJ Val",
    metaDescription:
      "DJ à Saint-Rémy-de-Provence (13) pour mariage dans les Alpilles. Mas, bastides, domaines viticoles. 20 ans d'expérience, devis 24 h.",
    h1: "DJ Mariage à Saint-Rémy-de-Provence",
    intro:
      "Saint-Rémy, capitale glamour des Alpilles. Ses mas en pierre, ses oliveraies, ses bastides nichées dans les massifs. Plus de 100 mariages animés dans le triangle Saint-Rémy / Eygalières / Maussane.",
    hero: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "À 25 minutes de Cavaillon. Saint-Rémy est ma deuxième zone d'intervention en volume, après Avignon.",
    highlights: [
      { title: "Spécialiste Alpilles", text: "Une centaine de mariages animés depuis 10 ans — chaque mas a sa logistique, je la connais." },
      { title: "Mariages haut de gamme", text: "Habitué d'une clientèle internationale exigeante : USA, UK, Suisse, Belgique. Communication bilingue." },
      { title: "Cérémonies laïques en plein air", text: "Sono cérémonie complète : micros HF, retour, musique d'entrée/sortie, gestion vent + soleil." },
    ],
    venues: ["Domaine de Manville", "Hôtel de Tourrel", "Mas de la Rose", "Château Romanin", "Mas des Comtes de Provence"],
    partners: [
      {
        name: "Domaine de Manville",
        type: "Domaine",
        desc: "5★ Relais & Châteaux aux Baux, golf et domaine pour mariages d'exception.",
        url: "https://www.domainedemanville.fr",
      },
      {
        name: "Château Romanin",
        type: "Château",
        desc: "Domaine viticole biodynamique au pied des Alpilles, réceptions dans le chai cathédrale.",
        url: "https://www.romanin.fr",
      },
      {
        name: "Office de Tourisme Saint-Rémy-de-Provence",
        type: "Office de Tourisme",
        desc: "Référence officielle des prestataires mariage des Alpilles.",
        url: "https://www.alpillesenprovence.com",
      },
    ],
    caterers: [
      { name: "Le Petit Duc", type: "Traiteur", desc: "Maison gastronomique saint-rémoise, prestations cocktail et mariage." },
      { name: "Mistral Gourmand Traiteur", type: "Traiteur", desc: "Traiteur haut de gamme des Alpilles, cuisine de saison et produits locaux." },
    ],
    faq: [
      { q: "Combien de mariages animez-vous à Saint-Rémy chaque année ?", a: "Entre 15 et 20 mariages par saison, principalement entre mai et octobre. Réservez 10 à 12 mois à l'avance." },
      { q: "Parlez-vous anglais pour les couples internationaux ?", a: "Oui, je peux mener une soirée bilingue français/anglais. Les annonces et interactions micro se font dans les deux langues si besoin." },
    ],
  },

  nimes: {
    slug: "nimes",
    name: "Nîmes",
    region: "Gard",
    title: "DJ Mariage Nîmes",
    metaTitle: "DJ Nîmes – Mariage & Soirée Gard | DJ Val",
    metaDescription:
      "DJ professionnel à Nîmes (30) pour mariage, anniversaire et événement d'entreprise. Mas, domaines viticoles, salles patrimoniales. Devis 24 h.",
    h1: "DJ Mariage à Nîmes",
    intro:
      "Nîmes, ville romaine entre Camargue et Cévennes. Ses arènes, ses mas gardois, ses domaines viticoles des Costières offrent un terrain magnifique pour les mariages. J'y interviens régulièrement depuis plus de 20 ans.",
    hero: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Nîmes est à 50 minutes de Cavaillon par l'A9. Couverture complète Gard : Uzès, Beaucaire, Tavel, Saint-Gilles, Sommières.",
    highlights: [
      { title: "Patrimoine et modernité", text: "Du Carré d'Art aux mas gardois, je m'adapte à tous les styles : minéral urbain ou champêtre garrigue." },
      { title: "Côtes du Rhône Gardoises", text: "Domaines de Tavel, Lirac, Costières de Nîmes — habitué des chais et caveaux pour réception." },
      { title: "Feria & culture taurine", text: "Habitué de l'ambiance feria, je sais doser entre tradition (sevillanas, pasodoble) et fête contemporaine." },
    ],
    venues: ["Domaine de Verchant", "Château Mongin", "Mas de l'Espérance", "Domaine de la Tour Vieille"],
    partners: [
      {
        name: "Château Mongin",
        type: "Château",
        desc: "Château viticole AOC Costières de Nîmes, espaces de réception jusqu'à 250 invités.",
        url: "https://www.chateau-mongin.com",
      },
      {
        name: "Office de Tourisme de Nîmes",
        type: "Office de Tourisme",
        desc: "Annuaire officiel des lieux de réception sur Nîmes et le Gard rhodanien.",
        url: "https://www.nimes-tourisme.com",
      },
    ],
    caterers: [
      { name: "Maison Brunel Traiteur", type: "Traiteur", desc: "Traiteur nîmois renommé, prestations mariage et événementiel sur-mesure." },
    ],
    faq: [
      { q: "Intervenez-vous dans tout le Gard ?", a: "Oui : Nîmes, Uzès, Beaucaire, Saint-Gilles, Tavel, Sommières, Anduze. Forfait déplacement transparent." },
      { q: "Animation pendant la Feria ?", a: "Oui, j'ai l'expérience des fêtes pendant la Feria : sélection adaptée, gestion du timing avec les corridas, ambiance hispanique sur demande." },
    ],
  },

  carpentras: {
    slug: "carpentras",
    name: "Carpentras",
    region: "Vaucluse",
    title: "DJ Mariage Carpentras",
    metaTitle: "DJ Carpentras : mariage Comtat | DJ Val",
    metaDescription:
      "DJ professionnel à Carpentras (84) pour mariage et soirée privée dans le Comtat Venaissin. Mas, domaines viticoles. 20 ans d'expérience, devis 24 h.",
    h1: "DJ Mariage à Carpentras",
    intro:
      "Carpentras, capitale du Comtat Venaissin, est entourée des plus beaux mas du Ventoux. Des Dentelles de Montmirail à Beaumes-de-Venise, j'y anime des mariages aux cadres provençaux d'exception.",
    hero: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "À 35 minutes de Cavaillon. Couverture : Carpentras, Pernes-les-Fontaines, Mazan, Bédoin, Caromb, Aubignan, Beaumes-de-Venise.",
    highlights: [
      { title: "Mas du Ventoux", text: "Plus de 50 mariages animés au pied du Mont Ventoux, dans les domaines de Mazan, Bédoin, Caromb." },
      { title: "Vins du Ventoux & Beaumes", text: "Habitué des caves coopératives et domaines familiaux des AOC Ventoux et Beaumes-de-Venise." },
      { title: "Ambiance comtadine", text: "Sélection qui valorise les classiques provençaux pour les invités locaux + modernité pour les jeunes générations." },
    ],
    venues: ["Domaine de la Verrière", "Château Pesquié", "Crillon le Brave", "Mas de Cure-Bourse"],
    partners: [
      {
        name: "Hôtel Crillon le Brave",
        type: "Salle",
        desc: "Relais & Châteaux au sommet d'un village perché du Ventoux, mariages d'exception.",
        url: "https://www.crillonlebrave.com",
      },
      {
        name: "Château Pesquié",
        type: "Château",
        desc: "Domaine viticole AOC Ventoux, espaces de réception et cuvées signature.",
        url: "https://www.chateaupesquie.com",
      },
      {
        name: "Office de Tourisme Ventoux-Provence",
        type: "Office de Tourisme",
        desc: "Annuaire officiel des salles, mas et hébergements du Comtat Venaissin.",
        url: "https://www.ventouxprovence.fr",
      },
    ],
    caterers: [
      { name: "Le Marijo Traiteur", type: "Traiteur", desc: "Traiteur comtadin reconnu, prestations mariage et cocktails." },
    ],
    sections: [
      {
        h2: "Les mas et domaines viticoles autour de Carpentras",
        paragraphs: [
          "Autour de Carpentras, la réception se fait presque toujours dehors : cour de mas, terrasse face au Ventoux, allée de platanes ou chai reconverti. Les domaines viticoles des AOC Ventoux et Beaumes-de-Venise ouvrent régulièrement leurs espaces, et chacun impose sa propre logique technique. Un chai en pierre demande un son court et maîtrisé ; une terrasse ouverte demande l'inverse, davantage de points de diffusion et une lumière qui délimite la piste dans le noir.",
          "Les villages de Mazan, Bédoin, Caromb, Aubignan et Pernes-les-Fontaines concentrent la majorité des lieux. L'alimentation électrique y est souvent le vrai sujet : beaucoup de mas fonctionnent sur des lignes anciennes, et je vérifie systématiquement la puissance disponible et la présence d'une ligne dédiée avant de dimensionner la sono et l'éclairage.",
        ],
      },
      {
        h2: "Mistral, plein air et fin de soirée dans le Comtat Venaissin",
        paragraphs: [
          "Au pied du Ventoux, le mistral n'est pas une anecdote : il peut se lever en une heure et déstabiliser un pied d'enceinte ou une structure d'éclairage. Sur toutes mes prestations à Carpentras et alentour, les enceintes sont lestées, les lyres fixées et le câblage protégé et plaqué au sol. Un plan de repli intérieur est validé avec le couple et le lieu dès la signature.",
          "L'autre spécificité locale, c'est la fraîcheur du soir : même en juillet, la température chute nettement après minuit dans les mas en altitude. J'organise alors la soirée pour que la piste se resserre progressivement, avec une bascule vers un espace couvert plutôt qu'une fin de soirée qui se vide.",
          "Côté horaires, les domaines du Comtat sont généralement plus souples qu'en centre-ville, mais plusieurs sont proches d'habitations : niveau surveillé au sonomètre et subwoofer orienté à l'opposé des voisins.",
        ],
      },
    ],
    caseStudy: {
      title: "Un mariage dans un domaine viticole du Ventoux",
      text: "Cérémonie sous les platanes, vin d'honneur dans les vignes, dîner en terrasse et soirée dans le chai. Le mistral s'est levé en fin d'après-midi : lestage complet, câbles plaqués et déplacement de la régie sous l'auvent avant le vin d'honneur. La soirée a basculé dans le chai à minuit, avec une diffusion réduite adaptée à la pierre et un éclairage resserré sur la piste — la piste est restée pleine jusqu'à la fin sans jamais gêner les habitations voisines.",
    },
    faq: [
      { q: "Intervenez-vous dans tout le Comtat Venaissin ?", a: "Oui : Carpentras, Pernes-les-Fontaines, Mazan, Bédoin, Sault, Beaumes-de-Venise, Vaison-la-Romaine." },
      { q: "Gestion du mistral au pied du Ventoux ?", a: "Oui, équipement sécurisé : enceintes lestées, lyres fixées, câblage protégé. J'ai l'habitude de ce type de configuration extérieure." },
      {
        q: "Peut-on organiser toute la soirée en extérieur dans un mas du Ventoux ?",
        a: "C'est possible, mais je prévois toujours un repli couvert : le mistral et la chute de température après minuit vident une piste en plein air plus vite qu'on ne l'imagine. La configuration idéale reste cérémonie et dîner dehors, soirée dansante sous un espace couvert ou dans un chai.",
      },
      {
        q: "L'électricité des domaines viticoles suffit-elle pour la sono et la lumière ?",
        a: "Dans la plupart des cas oui, mais je vérifie la puissance disponible et la présence d'une ligne dédiée avant le jour J. Sur les mas anciens autour de Mazan ou Caromb, j'adapte le matériel plutôt que de risquer une coupure en pleine soirée.",
      },
    ],

  },


  uzes: {
    slug: "uzes",
    name: "Uzès",
    region: "Gard",
    title: "DJ Mariage Uzès",
    metaTitle: "DJ Uzès – Mariage Duché & Pays d'Uzès Gard | DJ Val",
    metaDescription:
      "DJ professionnel à Uzès (30) pour mariage dans le Pays d'Uzès. Mas, domaines, châteaux. 20 ans d'expérience, devis personnalisé sous 24 h.",
    h1: "DJ Mariage à Uzès",
    intro:
      "Uzès, premier Duché de France, charme par ses ruelles médiévales et ses domaines alentour. J'y anime des mariages qui marient élégance languedocienne et énergie festive.",
    hero: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Uzès est à 1 h de Cavaillon. Couverture : Uzès, Pont du Gard, Saint-Quentin-la-Poterie, Vers-Pont-du-Gard, Collias.",
    highlights: [
      { title: "Lieux de prestige", text: "Mas et bastides du Pays d'Uzès, châteaux et hôtels particuliers du Duché." },
      { title: "Cadres patrimoniaux", text: "Habitué des cours intérieures pavées et des espaces médiévaux — sono adaptée à l'acoustique pierreuse." },
      { title: "Mariages internationaux", text: "Clientèle anglo-saxonne fréquente à Uzès : communication bilingue, animation adaptée." },
    ],
    venues: ["Domaine de Verchant", "Mas de Mèze", "Château d'Arpaillargues", "La Maison d'Uzès"],
    partners: [
      {
        name: "Château d'Arpaillargues – Maison d'Hôtes",
        type: "Château",
        desc: "Château du XVIIIᵉ avec parc, réceptions mariage jusqu'à 150 invités.",
        url: "https://www.lechateaudarpaillargues.com",
      },
      {
        name: "La Maison d'Uzès",
        type: "Salle",
        desc: "Hôtel 5★ Relais & Châteaux au cœur d'Uzès, mariages intimistes haut de gamme.",
        url: "https://www.lamaisonduzes.fr",
      },
      {
        name: "Office de Tourisme du Pays d'Uzès Pont du Gard",
        type: "Office de Tourisme",
        desc: "Annuaire officiel des lieux et prestataires mariage du Pays d'Uzès.",
        url: "https://www.destination-puregard.com",
      },
    ],
    caterers: [
      { name: "Le Tracteur Traiteur", type: "Traiteur", desc: "Traiteur réputé du Pays d'Uzès, cuisine de marché et prestations événementielles." },
    ],
    faq: [
      { q: "Intervenez-vous au Pont du Gard ?", a: "Oui, les réceptions au Pont du Gard sont sur mon parcours. Logistique adaptée à ce site classé UNESCO." },
      { q: "Animation bilingue pour mariage anglo-français ?", a: "Oui, micro en anglais et français, animation des temps forts dans les deux langues. Clientèle internationale fréquente à Uzès." },
    ],
  },








  pertuis: {
    slug: "pertuis",
    name: "Pertuis",
    region: "Vaucluse",
    title: "DJ Mariage Pertuis",
    metaTitle: "DJ Pertuis – Mariage Sud Luberon Vaucluse | DJ Val",
    metaDescription:
      "DJ professionnel à Pertuis (84) pour mariage en sud Luberon. Mas, châteaux, domaines viticoles AOC Luberon. Devis sous 24 h.",
    h1: "DJ Mariage à Pertuis",
    intro:
      "Pertuis, porte sud du Luberon entre Vaucluse et Bouches-du-Rhône. Des domaines viticoles AOC Luberon aux mas en pierre, j'y anime des mariages au croisement de la Provence et de la Durance.",
    hero: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Pertuis est à 30 minutes de Cavaillon. Couverture : Pertuis, La Tour-d'Aigues, Cadenet, Lourmarin, Cucuron, Ansouis.",
    highlights: [
      { title: "Sud Luberon", text: "Mariages réguliers à Lourmarin, Cucuron, Ansouis — villages perchés et mas typiques." },
      { title: "Châteaux viticoles", text: "Château de la Verrerie, Château Val Joanis, Domaine de la Cavale — terrain familier." },
      { title: "Sortie de bal en lavande", text: "Cadres champêtres exceptionnels en été, animation adaptée aux fêtes en pleine nature." },
    ],
    venues: ["Château Val Joanis", "Château de la Verrerie", "Domaine de la Cavale", "Bastide de la Brague"],
    partners: [
      {
        name: "Château Val Joanis",
        type: "Château",
        desc: "Domaine viticole AOC Luberon, jardins remarquables et salles de réception.",
        url: "https://www.val-joanis.com",
      },
      {
        name: "Château de la Verrerie",
        type: "Château",
        desc: "Domaine viticole familial AOC Luberon, espaces de réception jusqu'à 200 invités.",
        url: "https://www.chateau-la-verrerie.com",
      },
      {
        name: "Office de Tourisme Luberon Sud",
        type: "Office de Tourisme",
        desc: "Annuaire officiel des lieux de réception du sud Luberon.",
        url: "https://www.luberoncotedazur.com",
      },
    ],
    faq: [
      { q: "Intervenez-vous à Lourmarin et Cucuron ?", a: "Oui, ces villages sont dans ma zone privilégiée. Aucun supplément, mariages très fréquents." },
      { q: "Mariage en pleine vigne ou champ de lavande ?", a: "Aucun problème : groupe électrogène insonorisé sur demande, équipement adapté aux extérieurs." },
    ],
  },

  salon: {
    slug: "salon",
    name: "Salon-de-Provence",
    region: "Bouches-du-Rhône",
    title: "DJ Mariage Salon-de-Provence",
    metaTitle: "DJ Salon-de-Provence : mariage | DJ Val",
    metaDescription:
      "DJ professionnel à Salon-de-Provence (13) pour mariage entre Alpilles, Crau et étang de Berre. Mas, châteaux, domaines. Devis sous 24 h.",
    h1: "DJ Mariage à Salon-de-Provence",
    intro:
      "Salon-de-Provence, ville de Nostradamus, croise Alpilles, Crau et étang de Berre. Des mas en pierre aux domaines oléicoles, j'y interviens pour des mariages élégants et chaleureux.",
    hero: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=70&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Salon est à 45 minutes de Cavaillon. Couverture : Salon, Eyguières, Sénas, Lamanon, La Barben, Berre-l'Étang.",
    highlights: [
      { title: "Carrefour Provence", text: "Position centrale entre Alpilles, Aix et Camargue — habitué de tous les styles de mariage régionaux." },
      { title: "Domaines oléicoles", text: "Mariages réguliers dans les mas oléicoles emblématiques de la plaine salonaise." },
      { title: "Patrouille de France", text: "Salon abrite la Patrouille de France — clientèle militaire fréquente, sélection musicale qui sait honorer les traditions." },
    ],
    venues: ["Château de la Barben", "Mas de Pomeyrol", "Abbaye de Sainte-Croix", "Domaine de Calissanne"],
    partners: [
      {
        name: "Abbaye de Sainte-Croix",
        type: "Salle",
        desc: "Ancienne abbaye du XIIᵉ transformée en hôtel-restaurant, mariages d'exception.",
        url: "https://www.hotels-provence.com",
      },
      {
        name: "Domaine de Calissanne",
        type: "Domaine",
        desc: "Domaine viticole et oléicole AOC Coteaux d'Aix, espaces de réception jusqu'à 300 invités.",
        url: "https://www.calissanne.fr",
      },
      {
        name: "Office de Tourisme Salon-de-Provence",
        type: "Office de Tourisme",
        desc: "Annuaire officiel des lieux et prestataires mariage du pays salonais.",
        url: "https://www.visitsalondeprovence.com",
      },
    ],
    caterers: [
      { name: "Lenôtre Salon Traiteur", type: "Traiteur", desc: "Prestataire haut de gamme reconnu, prestations mariage personnalisées." },
    ],
    faq: [
      { q: "Intervenez-vous à Eyguières et La Barben ?", a: "Oui, c'est dans ma zone régulière. Aucun supplément." },
      { q: "Mariage militaire (Patrouille de France, Base aérienne) ?", a: "Oui, j'ai animé plusieurs mariages liés à la communauté militaire. Sélection respectueuse des traditions, capacité à intégrer les hymnes et moments protocolaires." },
    ],
  },

  // ============== ZONES DÉPARTEMENTALES & RÉGIONALES (SEO money) ==============

  vaucluse: {
    slug: "vaucluse",
    name: "Vaucluse",
    region: "Vaucluse (84)",
    preposition: "dans le",
    title: "DJ Mariage Vaucluse",
    metaTitle: "DJ mariage Vaucluse dès 1 490 € – Devis 24 h | DJ Val",
    metaDescription:
      "DJ mariage Vaucluse depuis 20 ans : 200+ mariages à Avignon, Cavaillon, Apt, Luberon. Sono et éclairage inclus, formules dès 1 490 €. Devis en 24 h, note 5,0.",



    h1: "DJ Mariage dans le Vaucluse",
    intro:
      "Référent DJ mariage dans le Vaucluse depuis 20 ans. Du Palais des Papes aux mas du Luberon, j'ai animé plus de 300 mariages dans le 84. Sonorisation Line Array RCF, 8 jeux d'éclairage LED, micros HF Shure, ouverture de bal aux étincelles froides — tout est inclus, rien n'est facturé en supplément.",
    hero: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1600&q=72&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Basé à Cavaillon, je couvre tout le département sans frais de déplacement : Avignon, Carpentras, Orange, Apt, Pertuis, Vaison-la-Romaine, L'Isle-sur-la-Sorgue, Gordes, Roussillon, Bonnieux, Ménerbes, Lacoste, Goult, Saignon.",
    highlights: [
      { title: "300+ mariages dans le Vaucluse", text: "Depuis 2010, j'ai animé plus de 300 mariages dans le 84. Aucun autre DJ local n'affiche ce volume avec une note Google 5/5." },
      { title: "Connaissance des domaines", text: "Mas de la Rose, Château La Verrerie, Coquillade Provence, Domaine de Tara, Mas de Capelans, Bastide de Marie — je connais chaque coin technique de ces lieux." },
      { title: "Limiteur calibré inclus", text: "Tous les domaines vauclusiens imposent un plafond dB. Je viens avec sonomètre et subwoofer directionnel pour respecter sans étouffer la fête." },
      { title: "1 seul mariage par week-end", text: "Je refuse les doubles dates : votre soirée est ma priorité absolue. Repérage du lieu offert dans le 84." },
      { title: "Réseau local 360°", text: "Photographes, traiteurs, wedding planners, fleuristes vauclusiens : un carnet d'adresses bâti sur 20 ans de collaborations." },
      { title: "Sono + LED inclus", text: "Pioneer CDJ-3000, DJM-A9, RCF Line Array, lyres LED, micros HF Shure. Pas de surprise, pas de supplément matériel." },
    ],
    venues: ["Mas de la Rose", "Château La Verrerie", "Coquillade Provence", "Domaine de Tara", "Mas de Capelans", "Bastide de Marie", "Château de Massillan", "Domaine de Manville", "Hôtel La Mirande"],
    partners: [
      { name: "Office de Tourisme Vaucluse Provence", type: "Office de Tourisme", desc: "Annuaire officiel des lieux de réception du département (84).", url: "https://www.provenceguide.com" },
      { name: "Coquillade Provence Resort & Spa", type: "Domaine", desc: "Resort 5★ panoramique sur le Luberon, jusqu'à 200 invités.", url: "https://www.coquillade.fr" },
      { name: "Château La Verrerie", type: "Château", desc: "Domaine viticole AOC Luberon, mariages d'exception au cœur du Vaucluse.", url: "https://www.chateau-la-verrerie.com" },
    ],
    faq: [
      { q: "Quel est le tarif moyen d'un DJ mariage dans le Vaucluse ?", a: "À partir de 1 490 € selon la durée (8 à 12 h) et les options. Formules tout-inclus : sono, éclairage LED, micros HF, installation, démontage. Aucun frais caché." },
      { q: "Couvrez-vous tout le département du Vaucluse ?", a: "Oui : Avignon, Cavaillon, Carpentras, Orange, Apt, Pertuis, Vaison-la-Romaine, L'Isle-sur-la-Sorgue, Sault, Bollène, Valréas, Gordes, Roussillon, Bonnieux. Aucun frais de déplacement dans tout le 84." },
      { q: "Combien de mariages avez-vous animés dans le Vaucluse ?", a: "Plus de 300 depuis 20 ans, soit 20 à 25 mariages vauclusiens par an. C'est mon cœur de zone historique." },
      { q: "Quand faut-il réserver pour un mariage dans le Vaucluse ?", a: "10 à 14 mois à l'avance pour mai–septembre. Les week-ends de juin et septembre se remplissent 18 mois à l'avance dans le Luberon." },
      { q: "Gérez-vous les contraintes sonores des mas du Luberon ?", a: "Oui systématiquement. Limiteur calibré, sonomètre, subwoofer directionnel, retours en façade — la fête reste pleine, le voisinage tranquille." },
      { q: "Pouvez-vous sonoriser une cérémonie laïque en extérieur ?", a: "Oui : micros HF Shure pour officiant et témoins, enceintes discrètes sur pied, musiques d'entrée/sortie validées en amont." },
    ],
  },

  provence: {
    slug: "provence",
    name: "Provence",
    region: "PACA · Provence",
    title: "DJ Mariage Provence",
    metaTitle: "DJ mariage Provence : Vaucluse & Luberon | DJ Val",
    metaDescription:
      "DJ mariage Provence : Vaucluse, Bouches-du-Rhône, Luberon, Alpilles, Aix-en-Provence, Avignon. 20 ans, 500+ événements, sono premium incluse. Devis 24 h.",
    h1: "DJ Mariage en Provence",
    intro:
      "DJ mariage en Provence depuis 20 ans. De Avignon aux Alpilles, du Luberon à Aix-en-Provence, je crée des soirées qui marquent les mémoires — avec un matériel premium, un sens du timing et une écoute totale de votre histoire.",
    hero: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1600&q=72&fm=webp&auto=compress&fit=crop",
    localInsight:
      "Mariages animés de Châteauneuf-du-Pape à Saint-Rémy, de Gordes à Aix, de Cassis à Uzès. Zone d'intervention sans frais : 80 km autour de Cavaillon.",
    highlights: [
      { title: "Cœur géographique idéal", text: "Basé à Cavaillon, à 30 min d'Avignon, Saint-Rémy, Aix, Apt. La Provence est mon terrain de jeu naturel depuis 20 ans." },
      { title: "500+ événements signés", text: "300+ mariages + 200+ soirées privées et corporate. Une expérience qui fait la différence dès le premier brief." },
      { title: "Esthétique provençale", text: "Du chic des bastides aux mariages bohèmes sous oliviers, je sais adapter l'ambiance au cadre — pas l'inverse." },
    ],
    venues: ["Mas de la Rose", "Château La Coste", "Coquillade Provence", "Villa Baulieu", "Domaine de Fontenille", "Château d'Estoublon", "Bastide de Marie"],
    faq: [
      { q: "Quelle zone couvrez-vous en Provence ?", a: "Vaucluse (84), Bouches-du-Rhône (13), Gard (30) et Alpes-de-Haute-Provence (04) limitrophes. Avignon, Aix, Arles, Nîmes, Salon, Uzès, Gordes, Saint-Rémy, Cavaillon, Apt." },
      { q: "Y a-t-il des frais de déplacement en Provence ?", a: "Aucun dans un rayon de 80 km autour de Cavaillon. Au-delà, un forfait transparent est intégré au devis." },
      { q: "Travaillez-vous avec les wedding planners provençaux ?", a: "Oui, partenariats réguliers avec les agences de Provence. Coordination fluide, ponctualité, discrétion." },
    ],
  },
};

export const CITY_SLUGS = Object.keys(CITIES);
